import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock } from "lucide-react";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      let targetPath = "/dashboard";
      if (user.role === "consultant") {
        targetPath = "/consultant";
      } else if (user.role === "community_member" || user.role === "community") {
        targetPath = "/community";
      } else if (user.role === "sponsor") {
        targetPath = "/sponsor-dashboard";
      } else if (user.role === "sponsorship_client" || user.sponsorship_purchased) {
        targetPath = "/sponsorship-dashboard";
      }
      navigate(targetPath, { replace: true });
    }
  }, [user, navigate]);

  // Detect mobile/browser info for debugging
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isChrome = /Chrome|CriOS/i.test(ua);
    return { ua: ua.substring(0, 150), isMobile, isSafari, isIOS, isAndroid, isChrome, platform: navigator.platform };
  };

  // Check if localStorage is available (Safari private mode blocks it)
  const isStorageAvailable = () => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (_e) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    
    const deviceInfo = getDeviceInfo();
    
    // Check storage availability early
    if (!isStorageAvailable()) {
      console.warn("[Login] localStorage not available - likely private browsing");
      if (deviceInfo.isIOS || deviceInfo.isSafari) {
        toast.warning("Private browsing detected. Some features may not work. Please use regular browsing mode.");
      }
    }
    
    try {
      // For iOS Safari, we need to ensure cookies work properly
      const deviceInfo_inner = getDeviceInfo();
      
      // Add timeout for mobile connections - 30 seconds
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      // Note: We use the JWT token from response body, not cookies
      // This avoids CORS credential issues with iOS Safari
      const fetchOptions = {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(form),
        signal: controller.signal,
        mode: "cors"
      };
      
      
      const response = await fetch(`${API}/auth/login`, fetchOptions);
      clearTimeout(timeoutId);
      
      
      if (response.ok) {
        const userData = await response.json();
        
        // Store token - with fallback for private browsing
        if (userData.token) {
          try {
            localStorage.setItem("auth_token", userData.token);
          } catch (storageErr) {
            console.warn("[Login] localStorage failed, using sessionStorage:", storageErr);
            try {
              sessionStorage.setItem("auth_token", userData.token);
            } catch (sessionErr) {
              console.error("[Login] All storage failed:", sessionErr);
              // Still proceed - the token is in the response and cookie
              if (deviceInfo.isIOS || deviceInfo.isSafari) {
                toast.warning("Private browsing mode detected. You may need to log in again if you close the app.");
              }
            }
          }
        }
        
        login(userData);
        toast.success("Welcome back!");
        
        // Navigate based on role and sponsorship status
        let targetPath = "/dashboard";
        if (userData.role === "consultant") {
          targetPath = "/consultant";
        } else if (userData.role === "community_member" || userData.role === "community") {
          targetPath = "/community";
        } else if (userData.role === "sponsor") {
          targetPath = "/sponsor-dashboard";
        } else if (userData.role === "sponsorship_client" || userData.sponsorship_purchased) {
          targetPath = "/sponsorship-dashboard";
        }
        navigate(targetPath);
      } else {
        let errorMsg = "Invalid credentials";
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (parseErr) {
          console.error("[Login] Failed to parse error:", parseErr);
          errorMsg = `Login failed (${response.status})`;
        }
        console.error("[Login] Error:", errorMsg, { status: response.status, device: deviceInfo });
        setLoginError({ message: errorMsg, status: response.status, device: deviceInfo });
        toast.error(errorMsg);
      }
    } catch (_err) {
      console.error("[Login] Network error:", _err.name, _err.message, { device: deviceInfo, apiUrl: API });
      
      // Provide more specific error messages based on error type
      let errorMsg;
      const isTimeout = _err.name === 'AbortError';
      const isNetworkError = _err.message.includes("Failed to fetch") || _err.message.includes("NetworkError") || _err.message.includes("Network request failed");
      const isCORSError = _err.message.includes("CORS") || _err.message.includes("cross-origin");
      
      if (isTimeout) {
        errorMsg = "Request timed out. Please check your connection and try again.";
      } else if (isCORSError) {
        errorMsg = "Connection blocked by security settings. Please try a different browser or contact support.";
        console.error("[Login] CORS Error - API URL:", API);
      } else if (isNetworkError) {
        if (deviceInfo.isIOS) {
          errorMsg = "Unable to connect. If using private browsing, try regular Safari. Check your internet connection.";
        } else {
          errorMsg = "Unable to connect to server. Please check your internet connection.";
        }
      } else {
        errorMsg = `Connection error: ${_err.message}`;
      }
      
      // Store detailed error for debugging
      setLoginError({ 
        message: _err.message, 
        type: _err.name,
        isTimeout,
        isNetworkError,
        isCORSError,
        device: deviceInfo,
        apiUrl: API
      });
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#333333] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to home</span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-semibold text-white leading-tight">
            Welcome Back to Your
            <span className="block text-[#39FF14]">Success Dashboard</span>
          </h1>
          <p className="text-neutral-300 text-lg max-w-md">
            Continue your journey towards business clarity and growth.
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
          <div>
            <p className="text-sm font-semibold text-white leading-tight">My Biz Consulting</p>
            <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to home</span>
            </Link>
          </div>
          
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-2">Sign In</h2>
            <p className="text-neutral-600">Enter your credentials to continue</p>
          </div>

          <Button 
            type="button"
            variant="outline" 
            className="w-full h-12 border-neutral-300 hover:bg-neutral-50 touch-manipulation"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-neutral-50 px-4 text-neutral-500">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-neutral-700">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#39FF14] hover:text-[#32E010] font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-neutral-600 hover:bg-neutral-500 text-white font-medium touch-manipulation"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            
            {/* Debug info for connection errors */}
            {loginError && loginError.type && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs">
                <p className="font-medium text-red-800 mb-1">Connection Details (for support):</p>
                <p className="text-red-600">Error: {loginError.message}</p>
                <p className="text-red-600">Type: {loginError.type}</p>
                {loginError.device && (
                  <p className="text-red-600">Device: {loginError.device.isIOS ? 'iOS' : loginError.device.isAndroid ? 'Android' : 'Desktop'} {loginError.device.isSafari ? 'Safari' : loginError.device.isChrome ? 'Chrome' : ''}</p>
                )}
                <p className="text-red-600 break-all">API: {loginError.apiUrl || API}</p>
              </div>
            )}
          </form>

          <p className="text-center text-neutral-600">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-[#39FF14] hover:text-[#32E010] font-medium" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
